// Sequencer step cell + 8-cell grid.
//
// Each cell exposes on/off, pitch offset and filter offset; the grid composes
// eight and supports current-step highlighting. Reads/writes StepState objects
// and notifies via an onChange(index, patch) callback. Standalone — wired to the
// engine sequencer in Phase 11.

import type { StepState } from '../../engine';

export class StepCell {
  readonly el: HTMLElement;
  private toggle!: HTMLButtonElement;
  private pitch!: HTMLInputElement;
  private filter!: HTMLInputElement;
  private state: StepState;

  constructor(
    readonly index: number,
    state: StepState,
    private readonly onChange: (index: number, patch: Partial<StepState>) => void,
  ) {
    this.state = { ...state };
    this.el = document.createElement('div');
    this.el.className = 'step';
    this.build();
    this.render();
  }

  private build(): void {
    this.toggle = document.createElement('button');
    this.toggle.className = 'step-toggle';
    this.toggle.type = 'button';
    this.toggle.title = 'Step on/off';
    this.toggle.addEventListener('click', () => {
      this.state.on = !this.state.on;
      this.toggle.dataset.on = String(this.state.on);
      this.onChange(this.index, { on: this.state.on });
    });

    this.pitch = document.createElement('input');
    this.pitch.type = 'range';
    this.pitch.min = '-24';
    this.pitch.max = '24';
    this.pitch.step = '1';
    this.pitch.title = 'Pitch offset (semitones)';
    this.pitch.addEventListener('input', () => {
      this.state.pitchOffset = Number(this.pitch.value);
      this.onChange(this.index, { pitchOffset: this.state.pitchOffset });
    });

    this.filter = document.createElement('input');
    this.filter.type = 'range';
    this.filter.min = '-1';
    this.filter.max = '1';
    this.filter.step = '0.05';
    this.filter.title = 'Filter offset';
    this.filter.addEventListener('input', () => {
      this.state.filterOffset = Number(this.filter.value);
      this.onChange(this.index, { filterOffset: this.state.filterOffset });
    });

    const num = document.createElement('div');
    num.className = 'step-num';
    num.textContent = String(this.index + 1);

    this.el.append(this.toggle, this.pitch, this.filter, num);
  }

  setState(state: StepState): void {
    this.state = { ...state };
    this.render();
  }

  setCurrent(current: boolean): void {
    this.el.dataset.current = String(current);
  }

  private render(): void {
    this.toggle.dataset.on = String(this.state.on);
    this.pitch.value = String(this.state.pitchOffset);
    this.filter.value = String(this.state.filterOffset);
  }
}

export class SequencerGrid {
  private readonly cells: StepCell[] = [];

  constructor(
    container: HTMLElement,
    steps: StepState[],
    onChange: (index: number, patch: Partial<StepState>) => void,
  ) {
    container.replaceChildren();
    steps.forEach((s, i) => {
      const cell = new StepCell(i, s, onChange);
      this.cells.push(cell);
      container.appendChild(cell.el);
    });
  }

  /** Highlight the active step (and clear the rest). */
  highlight(index: number): void {
    this.cells.forEach((c, i) => c.setCurrent(i === index));
  }

  setSteps(steps: StepState[]): void {
    steps.forEach((s, i) => this.cells[i]?.setState(s));
  }
}
